import {
  BaseEquipment,
  BenchAnchorPoint,
  EquipmentRequiringInstruction,
  EquipmentWeight,
  Gender,
  GymTypes,
  SmallBodyGroupType,
  StrengthRatioForMuscleGroups,
} from 'fitbeat.models';
import {
  Exercise,
  ExerciseFormat,
  IEquipmentWeightInfo,
  UserExerciseSession,
  WorkoutSettings,
} from '../../entities';
import { equipmentDescriptionMapping } from './equimpentWeightMaps';
import { getEquipmentResistanceStrategy } from './EquipmentResistanceStrategy';
import { NonQuadFormatWeightAdjustment } from './NonQuadFormatWeightAdjustment';
import { QuadFormatWeightAdjustment } from './QuadFormatWeightAdjustment';

export const defaultStrengthRatioForFemales = 0.8;
/**
 * The factor is 3, since moving up 1 resistance on assessment usually gives 1.333,
 * so 3*0.333 is 1 resistance level up
 */
export const ratioAdjustmentFactorForBands = 3;

export interface IEquipmentWeightForExerciseInZone {
  indexOfExercise: number;
  equipmentWeightInfo: IEquipmentWeightInfo;
  adjustedExerciseResistance: number;
  anchorPoint?: BenchAnchorPoint;
}

export type EquipmentWeightForExerciseInZone = {
  [key in EquipmentRequiringInstruction]?: IEquipmentWeightForExerciseInZone[];
};

export interface IWeightAndColor {
  weight: EquipmentWeight;
  color?: string;
}

export interface IWeightAdjustmentStrategy {
  adjustExerciseWeights(
    exerciseSession: UserExerciseSession,
    equipmentMap: EquipmentWeightForExerciseInZone,
  ): void;
}

export interface IResistanceStrategy {
  getEquipmentWeightAdjustedForCardioOrResistance(
    equipment: EquipmentRequiringInstruction,
    uniqueReferenceNumber: string,
    exerciseResistance: number,
    exerciseFormats: ExerciseFormat[],
    primaryMover: SmallBodyGroupType,
    settings: WorkoutSettings,
    user: IUserInfoForEquipmentWeight,
  ): { equipmentWeight: IWeightAndColor; adjustedExerciseResistance: number };
  getEquipmentWeight(
    equipment: EquipmentRequiringInstruction,
    uniqueReferenceNumber: string,
    exerciseResistance: number,
    primaryMover: SmallBodyGroupType,
    user: IUserInfoForEquipmentWeight,
  ): IWeightAndColor;
  calculateUserStrengthRatio(
    userWeight: number,
    exerciseWeight: number,
    equipmentRequiringInstruction?: EquipmentRequiringInstruction,
  ): number;
}

export interface IUserInfoForEquipmentWeight {
  gender: Gender;
  uid: string;
  strengthRatioForMuscleGroups?: StrengthRatioForMuscleGroups;
}

export class EquipmentWeightCalculator {
  public static getStrengthResistanceForUser(
    exerciseResistance: number,
    primaryMover: SmallBodyGroupType,
    user: IUserInfoForEquipmentWeight,
    uniqueReferenceNumber: string,
    ratioEquation: (exerciseResistance: number, userStrengthRatio: number) => number,
  ) {
    if (!exerciseResistance) {
      throw new Error(
        `No exercise resistnace set for exercise ${uniqueReferenceNumber}`,
      );
    }
    const defaultStrengthRatio = user.gender === Gender.Male ? 1 : defaultStrengthRatioForFemales;
    const userStrengthRatio = user?.strengthRatioForMuscleGroups;
    const userStrengthRatioForMuscleGroup = userStrengthRatio
      ? userStrengthRatio[primaryMover]
      : defaultStrengthRatio;
    const adjustedStrengthValue = ratioEquation(
      exerciseResistance,
      userStrengthRatioForMuscleGroup,
    );
    return adjustedStrengthValue;
  }

  private equipmentWeightForExerciseInZone: EquipmentWeightForExerciseInZone = {};

  private weightAdjustmentStrategy: IWeightAdjustmentStrategy;

  private resistanceStrategy: IResistanceStrategy;

  constructor(private gymType: GymTypes, private logger: any, format?: ExerciseFormat) {
    if (this.gymType === GymTypes.Home && format) {
      this.setWeightAdjustmentStrategy(format);
    }
  }

  public addEquipmentWeightInfoToUserExerciseSession(
    exerciseSession: UserExerciseSession,
    user: IUserInfoForEquipmentWeight,
    settings: WorkoutSettings,
  ) {
    try {
      const exercisesInZone = exerciseSession.exerciseInOrder();
      const exercisesWithEquipmentInfo = exercisesInZone.map((exercise, index) => {
        const exerciseCopy = structuredClone(exercise);
        try {
          if (this.shouldAssignEquipmentWeight(exerciseCopy)) {
            return this.assignEquipmentWeightToExercise(user, index, exerciseCopy, settings);
          }
          return exerciseCopy;
        } catch (e) {
          this.logger.error(`Error assigning equipment weight to exercise for user ${user.uid}`, e);
          return exerciseCopy;
        }
      });
      exerciseSession.setExercisesInOrder(exercisesWithEquipmentInfo);
      if (this.weightAdjustmentStrategy) {
        this.weightAdjustmentStrategy.adjustExerciseWeights(
          exerciseSession,
          this.equipmentWeightForExerciseInZone,
        );
      }
    } catch (e) {
      this.logger.error(`Error assigning equipment weight to exercise for user ${user.uid}`, e);
    }
  }

  private shouldAssignEquipmentWeight(exercise: Exercise) {
    const { equipmentWeightLevel } = exercise;
    const hasNoEquipment = !exercise.equipment || exercise.equipment.length === 0;
    if (hasNoEquipment) {
      return false;
    }
    const onlyContainsBench = BaseEquipment.onlyContainsBench(exercise.equipment);
    const equipmentWeightLevelIsBodyweight = equipmentWeightLevel === 'Bodyweight';
    const primaryMover = exercise.primaryMuscleMover;
    const primaryMoverNotSetOnExercise = !primaryMover;
    if (
      onlyContainsBench
      || BaseEquipment.isBodyweightOnly(exercise.equipment)
      || equipmentWeightLevelIsBodyweight
      || primaryMoverNotSetOnExercise
    ) {
      return false;
    }
    const equipmentRequiringInstruction = BaseEquipment.getEquipmentRequiringInstructionInfo(exercise.equipment);
    if (!equipmentRequiringInstruction) {
      return false;
    }
    return true;
  }

  private assignEquipmentWeightToExercise(
    user: IUserInfoForEquipmentWeight,
    indexOfExercise: number,
    exercise: Exercise,
    settings: WorkoutSettings,
  ): Exercise {
    const equipmentWeightLevel = exercise.equipmentWeightLevel as number;
    const primaryMover = exercise.primaryMuscleMover;
    const equipmentRequiringInstruction = BaseEquipment.getEquipmentRequiringInstructionInfo(exercise.equipment);
    if (!equipmentRequiringInstruction || !equipmentWeightLevel) {
      return exercise;
    }
    this.setResistanceStrategy(equipmentRequiringInstruction, this.gymType);
    const { equipmentWeight, adjustedExerciseResistance } = this.resistanceStrategy
      .getEquipmentWeightAdjustedForCardioOrResistance(
        equipmentRequiringInstruction,
        exercise.uniqueReferenceNumber,
        equipmentWeightLevel,
        exercise.exerciseFormats,
        primaryMover,
        settings,
        user,
      );
    const equipmentWeightInfo = {
      equipmentWeight: equipmentWeight.weight,
      color: equipmentWeight.color,
      description: equipmentDescriptionMapping.get(equipmentRequiringInstruction)!,
      equipment: equipmentRequiringInstruction,
    };
    exercise.equipmentWeightInfo = equipmentWeightInfo;
    const shouldUpdateEquipmentMapForZone = BaseEquipment.containsHomeBands(exercise.equipment);
    if (shouldUpdateEquipmentMapForZone) {
      this.updateEquipmentWeightForExerciseInZone(
        equipmentWeightInfo,
        indexOfExercise,
        equipmentRequiringInstruction,
        exercise,
        adjustedExerciseResistance,
      );
    }
    return exercise;
  }

  private setResistanceStrategy(equipmentRequiringInstruction: EquipmentRequiringInstruction, gymType: GymTypes) {
    this.resistanceStrategy = getEquipmentResistanceStrategy(
      gymType,
      equipmentRequiringInstruction,
    );
  }

  private updateEquipmentWeightForExerciseInZone(
    equipmentWeightInfo: IEquipmentWeightInfo,
    indexOfExercise: number,
    equipment: EquipmentRequiringInstruction,
    exercise: Exercise,
    adjustedExerciseResistance: number,
  ) {
    const anchorPoint = exercise.homeBenchSetupAnchorPoint?.length > 0
      ? exercise.homeBenchSetupAnchorPoint[0]
      : undefined;
    const equipmentInfo = {
      equipmentWeightInfo,
      indexOfExercise,
      anchorPoint,
      adjustedExerciseResistance,
    };
    if (!this.equipmentWeightForExerciseInZone[equipment]) {
      this.equipmentWeightForExerciseInZone[equipment] = [equipmentInfo];
      return;
    }
    this.equipmentWeightForExerciseInZone[equipment]!.push(equipmentInfo);
  }

  private setWeightAdjustmentStrategy(format: ExerciseFormat) {
    if (Exercise.isQuadFormat(format)) {
      this.weightAdjustmentStrategy = new QuadFormatWeightAdjustment();
    } else {
      this.weightAdjustmentStrategy = new NonQuadFormatWeightAdjustment();
    }
  }
}
